import React, { useMemo } from "react";
import { Avatar, Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { AvatarConfig } from "../types/user";
import {
  EquippedRewardState,
  getEquippedBadgeReward,
  getEquippedFrameReward,
  getEquippedHatAccessory,
  getRewardBadgeVisualValue,
  getRewardOverlayVisualValue,
  isImageSource,
  normalizeRewardCollection,
} from "../utils/rewards";

interface RewardedProfileDisplayProps {
  src?: string;
  alt: string;
  fallbackSrc: string;
  width: number | string;
  height?: number | string;
  variant?: "circle" | "portrait";
  border?: string;
  boxShadow?: string;
  backgroundColor?: string;
  rewards?: unknown;
  avatarConfig?: AvatarConfig;
  equippedState?: EquippedRewardState;
  containerSx?: SxProps<Theme>;
  imageSx?: SxProps<Theme>;
  badgeSize?: number;
  overlaySize?: number;
}

const RewardedProfileDisplay: React.FC<RewardedProfileDisplayProps> = ({
  src,
  alt,
  fallbackSrc,
  width,
  height,
  variant = "circle",
  border,
  boxShadow,
  backgroundColor,
  rewards,
  avatarConfig,
  equippedState,
  containerSx,
  imageSx,
  badgeSize,
  overlaySize,
}) => {
  const normalizedRewards = useMemo(
    () => normalizeRewardCollection(rewards),
    [rewards],
  );
  const equippedHat = avatarConfig
    ? getEquippedHatAccessory(avatarConfig)
    : null;
  const equippedFrameReward = getEquippedFrameReward(
    normalizedRewards,
    equippedState,
  );
  const equippedBadgeReward = getEquippedBadgeReward(
    normalizedRewards,
    equippedState,
  );
  const overlayValue =
    equippedHat?.image || getRewardOverlayVisualValue(equippedFrameReward);
  const badgeValue = getRewardBadgeVisualValue(equippedBadgeReward);
  const resolvedImage = src && src.trim() ? src : fallbackSrc;
  const resolvedBadgeSize = badgeSize ?? (variant === "circle" ? 22 : 28);
  const resolvedOverlaySize = overlaySize ?? (variant === "circle" ? 28 : 40);

  const renderVisual = (value: string, size: number) => {
    if (isImageSource(value)) {
      return (
        <Box
          component="img"
          src={value}
          alt="Recompensa equipada"
          sx={{ width: size, height: size, objectFit: "contain" }}
        />
      );
    }

    return (
      <Box
        component="span"
        sx={{
          fontSize: `${Math.max(16, size * 0.82)}px`,
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {value}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        width,
        height,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...containerSx,
      }}
    >
      {variant === "circle" ? (
        <Avatar
          src={resolvedImage}
          alt={alt}
          sx={{
            width: "100%",
            height: "100%",
            border,
            boxShadow,
            bgcolor: backgroundColor,
            ...imageSx,
          }}
        />
      ) : (
        <Box
          component="img"
          src={resolvedImage}
          alt={alt}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            border,
            boxShadow,
            borderRadius: 2.5,
            bgcolor: backgroundColor,
            ...imageSx,
          }}
        />
      )}

      {overlayValue ? (
        <Box
          sx={{
            position: "absolute",
            top: variant === "circle" ? -6 : 6,
            left: "50%",
            transform: "translateX(-50%)",
            minWidth: resolvedOverlaySize,
            minHeight: resolvedOverlaySize,
            px: isImageSource(overlayValue) ? 0 : 0.6,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          {renderVisual(overlayValue, resolvedOverlaySize)}
        </Box>
      ) : null}

      {badgeValue ? (
        <Box
          sx={{
            position: "absolute",
            right: variant === "circle" ? -2 : 8,
            bottom: variant === "circle" ? -2 : 8,
            width: resolvedBadgeSize,
            height: resolvedBadgeSize,
            borderRadius: "50%",
            background: "var(--dashboard-yellow)",
            border: "2px solid var(--dashboard-header-blue)",
            boxShadow: "0 2px 0 rgba(47, 43, 116, 0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
            overflow: "hidden",
          }}
        >
          {renderVisual(badgeValue, resolvedBadgeSize - 4)}
        </Box>
      ) : null}
    </Box>
  );
};

export default RewardedProfileDisplay;
